import {
  DownOutlined,
  EditOutlined,
  MessageOutlined,
  UpOutlined,
} from "@ant-design/icons";
import * as Sentry from "@sentry/react";
import { Button, Col, Row, Switch } from "antd";
import Acent from "components/Acent";
import Loading from "components/Loading";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import { useFirebase } from "containers/FirebaseContext";
import { onValue } from "firebase/database";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { get } from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useCurrentUser,
  useProfilePicturePicker,
  useUserProfileConfig,
} from "store/users/selectors";
import styled from "styled-components";
import { DebateComment, DebateNewLogo, EditDebate } from "utils/assets/images";
import { getAvatar } from "utils/assets/images/lootie";
import timerEndMp3 from "utils/assets/audio/timer_end.mp3";
import { v4 } from "uuid";
import EditStudentAnswer from "./editAnswerTeacher/EditStudentAnswer";
import { Star } from "@mui/icons-material";
import Tag from "components/Tag";
import SentryWrap from 'components/Sentry';
import JoinedStudents from "./joinedStudents";

const ResponseText = styled.div`
  margin-left: 20px;
`;
const ImageContainer = styled.span`
  position: absolute;
  left: -20px;
  top: 2px;
  img {
    width: 35px !important;
    height: 35px !important;
    border-radius: 50%;
  }
`;

const ReplyBoxWrapper = styled.div`
  // display: flex;
  // align-items: flex-start;
`;

const ProfileImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 10px;
  margin-top: 5px;
`;

const InputContainer = styled.div`
  flex: 1;
  // position: relative;
  background-color: white;
  border-radius: 18px;
  padding: 8px 12px;
  display: flex;
  align-items: flex-end;
  transition: all 0.2s ease-in-out;
  margin-bottom: 10px;
  @media Screen and (max-width: 390px) {
    max-width: 80%;
  }
  &: hover,
    ${({ isFocused }) =>
      isFocused &&
      `
    box-shadow: 0 0 0 1px #ccc;
    background-color: #fff;
  `};
`;

const StyledTextarea = styled.textarea`
  flex: 1;
  resize: none;
  border: none;
  background: transparent;
  font-size: 14px;
  // padding-right: 30px;
  min-height: 24px;
  max-height: 150px;
  overflow-y: auto;

  &:focus {
    outline: none;
  }
`;

const SendButton = styled.button`
  background: transparent;
  border: none;
  color: #1877f2;
  font-size: 25px;
  cursor: pointer;
  // position: absolute;
  bottom: 8px;
  right: 10px;

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;
const AvatarContainer = styled.div`
  p {
    font-size: 12px;
    margin-bottom: 0;
    font-weight: 700;
  }
  button {
    width: fit-content !important;
    min-width: inherit !important;
  }
  img {
    width: 25px !important;
    height: 25px !important;
    border-radius: 50%;
  }
`;

const Note = styled.span`
  color: #000;
  display: block;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
`;

const DebateCommentSection = ({
  studentSubmission: getStudentSubmision,
  debateResponse,
  timer,
  onEditTimer = () => null,
  isOpenEditMode,
  onClickOpen = () => {},
  updateStudentSubmission = () => null,
  activeStudentCount= []
}) => {
  const showAlert = useAlert();
  const { showProfilePhotoViewer, profile_picture_picker } =
    useProfilePicturePicker();
  const currentUser = useCurrentUser();
  const lastStatusRef = useRef();
  const editStudentReplyRef = useRef();
  const [enableEditMode, setEnableEditMode] = useState(false);
  const [showChatArea, setShowChatArea] = useState(false);
  const [showStudentNames, setShowStudentNames] = useState(false);
  const [editTime, setEditTime] = useState(false);
  const [isOpenComment, setOpenComment] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const configurations = get(debateResponse, "configurations", "") || {};
  const { profile_config } = useUserProfileConfig();
  const [editSubtitle, setEditSubtile] = useState({});
  const [editStudentReply, setEditStudentReply] = useState("");
  const [newIsFocused, setNewIsFocused] = useState(false);

  const updateConnectionStatus = useCallback(() => {
    if (!navigator.onLine) {
      setShowMessage(
        "You cannot send comments while offline. Please wait for your internet connection to be restored."
      );
      lastStatusRef.current = true;
    } else {
      setShowMessage("");
      lastStatusRef.current = false;
    }
  }, []);

  useEffect(() => {
    updateConnectionStatus(); // Initial check

    window.addEventListener("online", updateConnectionStatus);
    window.addEventListener("offline", updateConnectionStatus);

    const connection = navigator.connection;
    if (connection && connection.addEventListener) {
      connection.addEventListener("change", updateConnectionStatus);
    }

    return () => {
      window.removeEventListener("online", updateConnectionStatus);
      window.removeEventListener("offline", updateConnectionStatus);
      if (connection && connection.removeEventListener) {
        connection.removeEventListener("change", updateConnectionStatus);
      }
    };
  }, [updateConnectionStatus]);

  // Count the comments given by the specified user
  const studentSubmission = useMemo(() => {
    if (!getStudentSubmision?.length) return [];

    try {
      return [...getStudentSubmision].sort(
        (a, b) => new Date(b.answer_time) - new Date(a.answer_time)
      );
    } catch (error) {
      Sentry.captureException(
        `700 Error sorting student submissions: ${error}`
      );
      return [];
    }
  }, [getStudentSubmision]);

  const commentCount = useMemo(() => {
    if (!studentSubmission || studentSubmission.length === 0) return 0;

    try {
      return studentSubmission.reduce((total, answer) => {
        const userComments = answer?.comment?.filter(
          (comment) => comment?.user_id === get(currentUser, "id", "")
        );
        return total + (userComments?.length || 0);
      }, 0);
    } catch (error) {
      Sentry.captureException(`701 Error counting comments: ${error}`);
      return 0;
    }
  }, [studentSubmission]);

  const remainingReplies =
    parseInt(configurations?.total_replies) - commentCount;
  const [openedAccordions, setOpenedAccordions] = useState(() => {
    if (
      get(currentUser, "role") === "teacher" &&
      studentSubmission.length > 0
    ) {
      return studentSubmission.map((_, index) => index);
    }
    return [];
  });
  const { firestore, databaseRef, databaseSet, database } = useFirebase();
  const [commentValue, setCommentValue] = useState("");

  // useEffect(() => {
  //   if (
  //     get(currentUser, "role") === "teacher" &&
  //     studentSubmission.length > 0 &&
  //     openedAccordions.length === 0
  //   ) {
  //     setOpenedAccordions(studentSubmission.map((_, index) => index));
  //   }
  // }, [studentSubmission, currentUser]);
  const handleAccordionToggle = (index) => {
    if (get(currentUser, "role") === "teacher") {
      // For teachers, allow multiple accordions to be open/closed independently
      if (openedAccordions.includes(index)) {
        setOpenedAccordions((prev) => prev.filter((i) => i !== index));
        if (openedAccordions.length === 1) {
          onClickOpen("close");
        }
      } else {
        setOpenedAccordions((prev) => [...prev, index]);
        onClickOpen("open");
      }
    } else {
      if (openedAccordions.includes(index)) {
        setOpenedAccordions([]);
        onClickOpen("close");
        setCommentValue("");
        setShowChatArea(false);
      } else {
        setOpenedAccordions([index]);
        onClickOpen("open");
        setCommentValue("");
        setShowChatArea(true);
      }
    }
  };

  const getUserProfileImg = (img) => {
    if (img) return getAvatar(img);
    return DebateComment;
  };

  const EditComment = ({ comments, pIndex, index, handleEditClick = () => null }) => {
    const textareaCommentRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const [editStudentReplyy, setEditStudentReplyy] = useState('');

    const handleEditText = (text) => {
      setEditStudentReplyy(text);
    };

    useEffect(() => {
      if (comments?.reply_text) {
        setEditStudentReplyy(comments?.reply_text);
      }
    }, [comments]);

    return (
      <div className="flex-1">
        {get(currentUser, 'id', '') === comments?.user_id ? (
          <p>{comments?.user_name}</p>
        ) : (
          <p className="flex items-center justify-between">
            {showStudentNames
              ? `${comments?.user_name}`
              : `Student ${
                  studentSubmission?.findIndex((item) => item?.user_id == comments?.user_id) + 1
                }`}

            {get(currentUser, 'role') === 'teacher' && (
              <EditOutlined
                style={{ fontSize: '18px', color: '#555', marginLeft: '5px' }}
                onClick={() => {
                  if (
                    (enableEditMode && Object.keys(enableEditMode).length > 0) ||
                    (editSubtitle && Object.keys(editSubtitle).length > 0)
                  ) {
                    showAlert('Please save Your changes using enter key', 'error');
                    return;
                  }
                  setEditSubtile({});
                  onClickOpen('open');
                  handleEditClick(index, pIndex, comments?.reply_text);
                }}
              />
            )}
          </p>
        )}
        <div style={{ display: 'flex' }}>
          {enableEditMode?.index === index && enableEditMode?.pIndex === pIndex && (
            <div className="flex-1">
              <div className="flex items-center mt-10px widgetNote">
                <Tag height={20}>
                  <Star
                    style={{
                      height: '12px',
                      margin: '-1px 4px 0px 0px',
                      width: '12px',
                    }}
                  />
                  NOTE
                </Tag>

                <Note>Press Enter to save and see the updated message.</Note>
              </div>
              <InputContainer isFocused={isFocused}>
                <StyledTextarea
                  onFocus={() => setIsFocused(true)}
                  ref={textareaCommentRef}
                  placeholder="Write a reply..."
                  value={editStudentReplyy}
                  onChange={(e) => handleEditText(e.target.value)}
                  rows={3}
                  className="left-right-disabled"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (e.target.value?.length === 0) {
                        showAlert('Please enter a comment before submitting.', 'error');
                        return;
                      }
                      handleUpdateStudentComment(e.target.value, index, pIndex);
                    }
                  }}
                />
                <Acent
                  inputRef={textareaCommentRef}
                  setValue={setEditStudentReplyy}
                  style={{
                    marginBottom: 42,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    position: 'inherit',
                  }}
                />
                <SendButton
                  onClick={() => {
                    if (editSubtitle?.value?.length === 0) {
                      showAlert('Please enter a comment before submitting.', 'error');
                      return;
                    }
                    handleUpdateStudentComment(editStudentReplyy, index, pIndex);
                  }}
                >
                  &#9166;
                </SendButton>
              </InputContainer>
            </div>
          )}
        </div>
        <div
          style={{
            visibility:
              enableEditMode?.index === index && enableEditMode?.pIndex === pIndex
                ? 'hidden'
                : 'visible',
            height: enableEditMode?.index === index && enableEditMode?.pIndex === pIndex && '0px',
          }}
        >
          {comments?.reply_text}
          {comments?.isEdited ? ' (Comment Edited By Teacher)' : ''}
        </div>
      </div>
    );
  };

  const Accordion = ({
    num,
    title,
    children,
    subtitle = "",
    responseInfo,
    comments,
    configurations,
    openedAccordions,
    handleAccordionToggle,
    user_profile_img,
    commentValue,
    showChatArea,
    setShowChatArea,
    setOpenComment,
    isOpenComment,
    setEditSubtile,
    handleSubmitSubtitle,
  }) => {
    const [hideBtn, setHideBtn] = useState(true);
    const [showTextArea, setShowTextArea] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [editStudentComment, setEditStudentComment] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [editingTexts, setEditingTexts] = useState({});
    const [showError, setShowError] = useState(false);

    const handleIconClick = () => {
      setShowTextArea(true);
    };

    useEffect(() => {
      if (commentText === "" && commentValue) {
        setCommentText(commentValue);
      }
    }, [commentValue]);

    useEffect(() => {
      if (editSubtitle) {
        setEditStudentComment(editSubtitle?.value);
      }
    }, [editSubtitle]);

    const handleTextAreaChange = (text) => {
      setCommentText(text);
      setCommentValue(text);
    };

    const handleCommentSubmit = async () => {
      handleAccordionToggle();
      setOpenComment(true);
      // if (parseInt(configurations?.total_receive) < comments?.length) return;
      const userReplyData = {
        reply_time: moment().toISOString(),
        user_id: get(currentUser, "id", ""),
        user_name: `${get(currentUser, "firstName", "")} ${get(
          currentUser,
          "lastName",
          ""
        )}`,
        reply_text: commentText,
        response_id: get(responseInfo, "user_id", ""),
        user_profile_img: get(profile_config, "config.profile_pic", ""),
        comment_id: v4(),
      };
      await updateComments(userReplyData);
    };

    const handleKeyDown = (e) => {
      if (lastStatusRef?.current) return;
      setShowChatArea(false);
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault(); // prevent newline
        // Check if already submitting
        if (isSubmitting || !hideBtn) {
          return;
        }

        if (commentText.trim() && hideBtn) {
          setIsSubmitting(true);
          setHideBtn(false);
          setCommentValue("");
          handleCommentSubmit().finally(() => {
            // Reset submission state after completion or error
            setIsSubmitting(false);
          });
        }
      }
    };

    const updateComments = async (comment) => {
      const user_profile_imgs = get(profile_config, "config.profile_pic", "");
      const generated_session_id = comment?.response_id + debateResponse?.id;
      const docRef = doc(firestore, "debate_submission", generated_session_id);
      try {
        // Start the transaction
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const existingData = docSnap.data();
          const existingComments = existingData.comment || [];
          comment.user_profile_img = user_profile_imgs;
          const updatedComments = [...existingComments, comment];
          await updateDoc(docRef, { comment: updatedComments });
          setTimeout(() => {
            setCommentValue("");
            setHideBtn(true);
            onClickOpen("close");
            setOpenComment(false);
          }, 2000);
        }
      } catch (e) {
        Sentry.captureException(`561 Error adding document: ${e}`);
        console.log("Error adding document: ", e);
      }
    };

    const responseComments = get(responseInfo, "comment") || [];
    const findResponse = responseComments.some(
      (itm) => itm.user_id === get(currentUser, "id", "")
    );
    const textareaCommentRef = useRef(null);
    const filterBonus = comments?.filter((k) => k?.is_bonus) || [];
    const filterNoBonus = comments?.filter((k) => !k?.is_bonus) || [];
    const newComment = comments
      ?.slice()
      ?.sort((a, b) => new Date(a.reply_time) - new Date(b.reply_time)) // earliest first
      ?.slice(0, parseInt(configurations?.total_receive));
    const allBonus = comments.filter(
      (item) => !newComment.some((s) => s.comment_id === item.comment_id)
    );
    const allNoBonus = comments.filter((item) =>
      newComment.some((s) => s.comment_id === item.comment_id)
    );

    return (
      <div className="accordionWrapper">
        <div
          className={`accordionTitle ${
            openedAccordions.includes(num) ? "open" : ""
          }`}
          onClick={(e) => {
            if (currentUser?.role === "student") {
              handleAccordionToggle(num);
            }
            if (editSubtitle?.index === num) return;
          }}
        >
          <div className="acordCont">
            <div
              style={{
                width: editSubtitle?.index === num ? "84%" : "fit-content",
              }}
              className="debateHeader"
            >
              <ImageContainer>
                <img src={user_profile_img} alt="Debate" />
              </ImageContainer>

              <div  className="debateContent">
                <div
                  onClick={() => {
                    if (currentUser?.role === "teacher") {
                      setEditSubtile({});
                      handleAccordionToggle(num);
                    }
                  }}
                  className="flex items-center justify-between"
                >
                  <p>{title}</p>
                
                    <div className="flex items-center cursor-pointer">
                      {comments?.length > 0 && (
                        <div>
                          {openedAccordions?.includes(num) ? (
                            <UpOutlined style={{ fontSize: "16px" }} />
                          ) : (
                            <DownOutlined style={{ fontSize: "16px" }} />
                          )}
                        </div>
                      )}
                        </div>

                  {currentUser?.role !== "student" && (
                    <>
                      {(get(currentUser, "role") === "teacher" && !isOpenEditMode) && (
                        <EditOutlined
                          style={{
                            fontSize: "18px",
                            color: "#555",
                            marginLeft: "5px",
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (
                              (editSubtitle &&
                                Object.keys(editSubtitle).length > 0) ||
                              (enableEditMode &&
                                Object.keys(enableEditMode).length > 0)
                            ) {
                              showAlert("Please Save Your changes using enter key.", "error");
                              return;
                            }
                            if (currentUser?.role === "teacher") {
                              setEnableEditMode({});
                              onClickOpen("open");
                              setEditSubtile({ index: num, value: subtitle });
                              setShowError(false);
                            }
                          }}
                        />
                      )}
                  </>
                  )}
                </div>

                <div className="stdntComntTopShow">
                  <EditStudentAnswer
                    states={{
                      subtitle,
                      num,
                      editSubtitle,
                      editingTexts,
                      showError,
                    }}
                    funcObj={{ handleSubmitSubtitle }}
                  />
                  {allBonus?.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        right: 77,
                        top: 0,
                        backgroundImage:
                          "radial-gradient(circle, #fff 40%, #ffeb3b 53%, #e00162 29%)",
                      }}
                      className={`stdntComntShow`}
                    >
                      <label>{allBonus?.length || 0}</label>
                    </div>
                  )}
                  {allBonus?.length > 0 && (
                    <img
                      style={{
                        marginLeft: 4,
                        height: "30px",
                        width: "30px",
                        position: "absolute",
                        right: 22,
                        top: 0,
                        borderRadius: "unset",
                      }}
                      src={
                        "https://imagedelivery.net/B62pqpMZqieDKhv80nP5iw/cb3449bf-5f30-4463-c594-590f0c257100/thumbnail"
                      }
                      alt="Assignment Ai"
                    />
                  )}
                  {!isOpenComment &&
                    hideBtn &&
                    !findResponse &&
                    remainingReplies > 0 &&
                    get(currentUser, "role") === "student" &&
                    responseInfo?.user_id !== get(currentUser, "id", "") &&
                    parseInt(configurations?.total_receive) >
                      comments?.length && (
                      <div
                        className="commentIcon"
                        style={{ position: "absolute", top: -8, right: 35 }}
                      >
                        <Button
                          onClick={() => handleIconClick()}
                          shape="circle"
                          disabled={timer === "00:00" ? true : false}
                        >
                          <MessageOutlined className="messageIcon" />
                        </Button>
                      </div>
                    )}
                  <div
                    style={{ position: "absolute", right: 0, top: 0 }}
                    className={`stdntComntShow ${
                      parseInt(configurations?.total_receive) <=
                      allNoBonus?.length
                        ? "stdntComntShowDone"
                        : ""
                    }`}
                  >
                    <label>{allNoBonus?.length || 0}</label>
                  </div>
                </div>
                <div
                  style={{
                    visibility:
                      editSubtitle?.index === num ? "hidden" : "visible",
                    height: editSubtitle?.index === num && "0px",
                  }}
                >
                  {
                    <span dangerouslySetInnerHTML={{ __html: subtitle || '' }} />
                  } {" "}
                  {responseInfo?.answer_edited
                    ? " (Answer Edited By Teacher)"
                    : ""}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`accordion-item ${
            openedAccordions.includes(num) ? "" : "collapsed"
          }`}
        >
          {children?.length > 0 && (
            <div className="accordion-content">{children}</div>
          )}

          {!isOpenComment &&
            hideBtn &&
            !findResponse &&
            remainingReplies > 0 &&
            get(currentUser, "role") === "student" &&
            responseInfo?.user_id !== get(currentUser, "id", "") &&
            parseInt(configurations?.total_receive) > comments?.length &&
            openedAccordions.includes(num) &&
            remainingReplies > 0 && (
              <ReplyBoxWrapper>
                {lastStatusRef?.current === true && (
                  <div className="studentReplyTxt">
                    <span>
                      {
                        "You cannot send comments while offline. Please wait for your internet connection to be restored."
                      }
                    </span>
                  </div>
                )}
                <div
                  style={{ display: "flex", marginTop: "12px", marginLeft: 25 }}
                >
                  <ProfileImage
                    src={getUserProfileImg(
                      get(profile_config, "config.profile_pic", "")
                    )}
                    alt="User"
                  />
                  <InputContainer isFocused={isFocused}>
                    <StyledTextarea
                      ref={textareaCommentRef}
                      placeholder="Write a reply..."
                      value={commentText}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      onChange={(e) => handleTextAreaChange(e.target.value)}
                      rows={3}
                      onKeyDown={handleKeyDown}
                    />
                    <Acent
                      inputRef={textareaCommentRef}
                      setValue={handleTextAreaChange}
                      style={{
                        marginBottom: 42,
                        display: "flex",
                        justifyContent: "flex-end",
                        position: "inherit",
                      }}
                    />
                    {(commentText.trim() ||
                      !hideBtn ||
                      !lastStatusRef?.current) && (
                      <SendButton
                        onClick={() => {
                          if (lastStatusRef?.current === true) return;
                          setShowChatArea(false);
                          setHideBtn(false);
                          handleCommentSubmit();
                        }}
                        disabled={!hideBtn || commentText?.length === 0}
                      >
                        &#9166;
                      </SendButton>
                    )}
                  </InputContainer>
                </div>
              </ReplyBoxWrapper>
            )}
        </div>
      </div>
    );
  };

  const handleUpdateStudentComment = (value, sIndex, cindex) => {
    try {
      const studentSubmissions = [...studentSubmission];
      const updatedStudent = studentSubmissions[sIndex];
      updatedStudent.teacher_reply = true;
      updatedStudent.comment[cindex].reply_text = value;
      updatedStudent.comment[cindex].isEdited = true;
      updateStudentSubmission(updatedStudent, updatedStudent?.user_id);
      setEnableEditMode({});
      setEditSubtile({});
      onClickOpen("close");
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error updating student comment:", error);
    }
  };

  const handleSubmitSubtitle = (value, index) => {
    try {
      const studentSubmissions = [...studentSubmission];
      const updatedStudent = studentSubmissions[index];
      updatedStudent.teacher_reply = true;
      updatedStudent.answer_text = value;
      updatedStudent.answer_edited = true;
      updateStudentSubmission(updatedStudent, updatedStudent?.user_id);
      setEnableEditMode({});
      setEditSubtile({});
      onClickOpen("close");
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
    }
  };


  const handleEditSubtitle = (value, index) => {
    setEditSubtile((prev) => ({
      ...prev,
      index,
      value: value,
    }));
  };

  const handleEditClick = (index, pIndex, reply_text) => {
    setEnableEditMode({ index, pIndex });
    setEditStudentReply(reply_text); // Initial value
    editStudentReplyRef.current = reply_text;
  };
  const commentRender = useMemo(() => {
    return (
      <>
        <div className="studentsubmissionbg">
          {studentSubmission.map((debate, index) => {
            const newComment = debate?.comment
              ?.slice()
              ?.sort((a, b) => new Date(a.reply_time) - new Date(b.reply_time)) // earliest first
              ?.slice(0, parseInt(configurations?.total_receive));
            return (
              <Accordion
                num={index}
                title={
                  showStudentNames ||
                  get(currentUser, "id", "") === debate?.user_id
                    ? `${debate?.user_name}`
                    : `Student ${index + 1}`
                }
                user_profile_img={getUserProfileImg(debate?.user_profile_img)}
                subtitle={debate?.answer_text}
                responseInfo={debate}
                comments={debate?.comment}
                configurations={configurations}
                openedAccordions={openedAccordions}
                commentValue={commentValue}
                handleAccordionToggle={handleAccordionToggle}
                showChatArea={showChatArea}
                setShowChatArea={setShowChatArea}
                setOpenComment={setOpenComment}
                isOpenComment={isOpenComment}
                setEditSubtile={setEditSubtile}
                editSubtitle={editSubtitle}
                handleEditSubtitle={handleEditSubtitle}
                handleSubmitSubtitle={handleSubmitSubtitle}
              >
                {debate?.comment?.map((comments, pIndex) => {
                  return (
                    <div
                      key={pIndex}
                      className="createCollapse studentCommentSec "
                      style={{
                        display: "flex",
                        marginBottom: 10,
                        width:
                          enableEditMode?.index === index &&
                          enableEditMode?.pIndex === pIndex
                            ? "84%"
                            : "fit-content",
                        borderRadius: 15,
                        padding: 8,
                        marginLeft: 35,
                        background: "white",
                        position: "relative",
                        maxWidth: "83%",
                      }}
                    >
                      <img
                        src={getUserProfileImg(comments?.user_profile_img)}
                        alt="Debate"
                        className="submissionnotification"
                      />
                      {!newComment?.some(
                        (c) => c?.comment_id === comments?.comment_id
                      ) && (
                        <img
                          style={{
                            height: "30px",
                            width: "30px",
                            position: "absolute",
                            right: 22,
                            top: 30,
                            borderRadius: "unset",
                          }}
                          src={
                            "https://imagedelivery.net/B62pqpMZqieDKhv80nP5iw/cb3449bf-5f30-4463-c594-590f0c257100/thumbnail"
                          }
                          alt="Assignment Ai"
                        />
                      )}
                      <EditComment
                        comments={comments}
                        index={index}
                        pIndex={pIndex}
                        handleEditClick={handleEditClick}
                      />
                    </div>
                  );
                })}
              </Accordion>
            );
          })}
        </div>
      </>
    );
  }, [
    studentSubmission,
    showStudentNames,
    openedAccordions,
    lastStatusRef?.current,
    enableEditMode,
    editSubtitle,
  ]);

  const getTimeFromDate = (timer) => {
    if(timer){
      const timeComponents = timer?.split(":");
      const minutes = parseInt(timeComponents[0]); // Extract minutes part
      const seconds = parseInt(timeComponents[1]); // Extract seconds part
      const totalTimeInSeconds = minutes * 60 + seconds; // Convert minutes to seconds and add seconds
      return totalTimeInSeconds;
    }
    return 0;
  };

  const audio = new Audio(timerEndMp3);
  audio.volume = 1;

  useEffect(() => {
    if (get(currentUser, "role", "") === "teacher") {
      if (getTimeFromDate(timer) <= 0) {
        audio.pause();
      } else if (getTimeFromDate(timer) <= 5) {
        audio.play();
      } else {
      }
    }
  }, [timer, audio]);

  useEffect(() => {
    if (Object.keys(debateResponse).length > 0) {
      const timerRef = databaseRef(
        database,
        "debateStatus/" + debateResponse.id
      );
      const unsubscribe = onValue(timerRef, (snapshot) => {
        try {
          const statusData = snapshot.val();
          setShowStudentNames(statusData?.showStatus);
        } catch (error) {
          console.log("error in database timer", error);
          Sentry.captureException(
            `128 Error during Firebase debate status onValue ${error}.`
          );
        }
      });
      return () => unsubscribe();
    }
  }, [database, debateResponse]);

  const showStudentName = async (check) => {
    const dbRef = databaseRef(database, "debateStatus/" + debateResponse.id);
    databaseSet(dbRef, { showStatus: check })
      .then((res) => {})
      .catch((error) => {
        console.log("Error updating time in the database:", error);
        Sentry.captureException(
          `129 Error during Firebase debate showStudentName ${error}.`
        );
      });
  };

  return (
    <SentryWrap>
    <div className="ideaMdlUpr">
      <Row>
        <Col xs={24}>
          <div className="allDebateCommitSection">
            <div className="debateMdlTop debateAllTopTxt studentCommitModal debateAllTopSpace">
              <img src={DebateNewLogo} alt="Debate" />
              <ResponseText>
                <span className="breakTheSentence">
                  {debateResponse?.question}
                </span>
              </ResponseText>
            </div>
            <div className="stdntTopCommitSec">
              <div className="studntCommitSection">
                {get(currentUser, "role") === "student" && (
                  <>
                    <AvatarContainer className="blogInputBox">
                      <div style={{ marginRight: 12 }}>
                        <button
                          onClick={() => {
                            showProfilePhotoViewer();
                          }}
                        >
                          {getUserProfileImg(
                            get(profile_config, "config.profile_pic", "")
                          ) ? (
                            <img
                              src={getUserProfileImg(
                                get(profile_config, "config.profile_pic", "")
                              )}
                              alt="Debate"
                              style={{ width: 20, height: 20 }}
                            />
                          ) : (
                            <Loading size={18} />
                          )}
                        </button>
                      </div>
                    </AvatarContainer>
                  </>
                )}
                <div
                  id="new-timer"
                  className={
                    getTimeFromDate(timer) <= 0
                      ? "blackTimerSection"
                      : getTimeFromDate(timer) <= 5 &&
                        get(currentUser, "role", "") === "teacher"
                      ? "stduntTimerSection"
                      : ""
                  }
                >
                  <span>{timer}</span>
                </div>
                {editTime && getTimeFromDate(timer) > 0 && (
                  <div className="blogInputBox">
                    <div style={{ marginRight: 12 }}>
                      <button
                        onClick={() => {
                          if (lastStatusRef?.current) return;
                          onEditTimer(1 * 60);
                        }}
                      >
                        + 1
                      </button>
                    </div>
                  </div>
                )}
                {get(currentUser, "role") === "teacher" &&
                  getTimeFromDate(timer) > 0 && (
                    <img
                      style={{ cursor: "pointer" }}
                      src={EditDebate}
                      alt="Debate"
                      onClick={() => {
                        setEditTime(!editTime);
                      }}
                    />
                  )}
              </div>
              {get(currentUser, "role") !== "teacher" && (
                <div className="debatcommitTxtLeft">
                  <span>How many REPLIES left:</span>
                  <div
                    className={`stdntComntShow ${
                      parseInt(remainingReplies) === 0
                        ? "stdntComntShowDone"
                        : ""
                    }`}
                  >
                    <label>{remainingReplies}</label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Col>
        {currentUser?.role === "teacher" && (
        <div className="flex items-center justify-end gap-10px w-full mt-10px mb-10px">
                <JoinedStudents debateResponse={debateResponse} />
                  <div className="debatcommitTxt flex items-center">
                    <span style={{ whiteSpace: "nowrap" }}>Show student names</span>
                    <Switch
                      checked={showStudentNames}
                      onClick={(checked) => {
                        if(Object.keys(editSubtitle)?.length > 0){
                          showAlert("Please save Your changes using enter key", "error");
                          return;
                        }
                        showStudentName(checked);
                      }}
                      disabled={Object.keys(editSubtitle)?.length > 0}
                    />
                  </div>
                </div>
        )}
        <Col xs={24}>
          {get(currentUser, "role") === "student" && (
            <div className="studentReplyTxt">
              <span>You can only post one reply per student</span>
            </div>
          )}
          <div className="allLiveDebateSec">
            <div className="createCollapse">
              {studentSubmission.length === 0 ? (
                <div className="studentResponceSecLiveDebate">
                  Waiting for students response...
                </div>
              ) : (
                <div className="studentsubmissionmainbg">{commentRender}</div>
              )}
            </div>
          </div>
        </Col>
      </Row>
      </div>
    </SentryWrap>
  );
};

export default DebateCommentSection;

const StyledTextCommentArea = styled.input`
  flex: 1;
  resize: none;
  width: 100%;
  min-height: 100px;
  overflow: auto;
`;
